import { Link, useSearchParams } from "react-router-dom";
import { newsList } from "@/mocks/news";
import { paginate } from "@/utils/catalog";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Pagination from "@/components/feature/Pagination";
import usePageMeta from "@/hooks/usePageMeta";

const PAGE_SIZE = 5;

export default function NewsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get("page") || "1") || 1;

  usePageMeta({
    title: "电影资讯 - 电影天堂最新影视动态",
    description:
      "电影天堂资讯频道，提供最新电影票房、行业动态、幕后观察与影片上映资讯。",
    keywords: "电影天堂,电影资讯,影视动态,票房,电影新闻",
  });

  const { items, totalPages, currentPage, total } = paginate(newsList, page, PAGE_SIZE);
  const [featured, ...rest] = items;

  const updatePage = (next: number) => {
    const params = new URLSearchParams(searchParams);
    if (next <= 1) params.delete("page");
    else params.set("page", String(next));
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
          <nav className="mb-4 flex flex-wrap items-center gap-1 text-sm text-foreground-400" aria-label="面包屑">
            <Link to="/" className="hover:text-accent-400">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-100">电影资讯</span>
          </nav>

          <h1 className="text-2xl font-bold text-foreground-50 md:text-3xl">电影资讯</h1>
          <p className="mt-2 text-sm text-foreground-400">
            最新影视动态、票房榜单与幕后观察，共 {total} 篇资讯
          </p>

          {featured && (
            <Link
              to={`/news/${featured.id}`}
              className="group mt-8 grid grid-cols-1 overflow-hidden rounded-lg bg-background-100 ring-1 ring-background-200/70 transition hover:ring-accent-500/60 md:grid-cols-2"
            >
              <div className="h-52 w-full overflow-hidden bg-background-200 md:h-full">
                <img
                  src={featured.cover}
                  alt={`${featured.title}配图`}
                  title={`${featured.title} - 电影天堂资讯`}
                  className="h-full w-full object-top transition duration-500 group-hover:scale-105"
                />
              </div>
              <div className="flex flex-col justify-center p-6 md:p-8">
                <span className="w-fit rounded-md bg-accent-500 px-2 py-0.5 text-xs font-bold text-background-950">
                  头条
                </span>
                <h2 className="mt-3 text-xl font-bold text-foreground-50 transition group-hover:text-accent-400 md:text-2xl">
                  {featured.title}
                </h2>
                <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-foreground-400">
                  {featured.summary}
                </p>
                <p className="mt-4 flex items-center gap-1 text-xs text-foreground-500">
                  <i className="ri-calendar-line" />
                  {featured.date}
                </p>
              </div>
            </Link>
          )}

          <div className="mt-6 space-y-3">
            {rest.map((item) => (
              <Link
                key={item.id}
                to={`/news/${item.id}`}
                className="group flex items-start gap-4 rounded-lg bg-background-100 p-4 ring-1 ring-background-200/70 transition hover:ring-accent-500/60"
              >
                <div className="hidden h-24 w-36 shrink-0 overflow-hidden rounded-md bg-background-200 sm:block">
                  <img
                    src={item.cover}
                    alt={`${item.title}配图`}
                    title={`${item.title} - 电影天堂资讯`}
                    className="h-full w-full object-top transition duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-base font-medium text-foreground-100 transition group-hover:text-accent-400">
                    {item.title}
                  </h3>
                  <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-400">
                    {item.summary}
                  </p>
                  <p className="mt-3 flex items-center gap-1 text-xs text-foreground-500">
                    <i className="ri-calendar-line" />
                    {item.date}
                  </p>
                </div>
                <i className="ri-arrow-right-s-line hidden self-center text-xl text-foreground-500 transition group-hover:text-accent-400 md:block" />
              </Link>
            ))}
          </div>

          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={updatePage} />
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}